// ============================================================================
// Qualité / provenance dataV2 (DEV-595) — SSOT front partagé
// ----------------------------------------------------------------------------
// Mapping du code provenance `t` (index dans `meta.provenance` renvoyé par la
// route `POST /source/datav2/series`) → libellé + couleur. Partagé par le
// tableau (C1), le badge (C2) et le graphe (C3) pour un rendu cohérent.
//
// Légende `meta.provenance` (ordre = code) :
//   0 raw_publiee_definitive · 1 raw_publiee_provisoire · 2 raw_informative_definitive
//   3 raw_informative_provisoire · 4 raw_*_autres · 5 computed
// Fiabilité : publiée > informative, définitive > provisoire.
// ============================================================================

export type ProvenanceCode = 0 | 1 | 2 | 3 | 4 | 5

export type QualityDescriptor = {
    /** libellé lisible (ex. "Publiée · Définitive") */
    label: string
    /** couleur du texte (badge) */
    color: string
    /** couleur de fond (badge) */
    bg: string
    /** couleur pleine pour les points/segments de graphe */
    plot: string
    /**
     * Opacité de la donnée sur les graphes : la QUALITÉ est encodée en opacité
     * (la couleur, elle, sert à distinguer les séries → palette maison).
     * Définitive = plein, provisoire/informatif = atténué.
     */
    opacity: number
}

/** Mapping code → descripteur. Ordre aligné sur `meta.provenance`. */
export const QUALITY: Record<ProvenanceCode, QualityDescriptor> = {
    0: {
        label: "Publiée · Définitive",
        color: "#15803d",
        bg: "#dcfce7",
        plot: "#16a34a",
        opacity: 1,
    },
    1: {
        label: "Publiée · Provisoire",
        color: "#b45309",
        bg: "#fef3c7",
        plot: "#f59e0b",
        opacity: 0.55,
    },
    2: {
        label: "Informative · Définitive",
        color: "#1d4ed8",
        bg: "#dbeafe",
        plot: "#3b82f6",
        opacity: 0.75,
    },
    3: {
        label: "Informative · Provisoire",
        color: "#475569",
        bg: "#f1f5f9",
        plot: "#94a3b8",
        opacity: 0.4,
    },
    4: {
        label: "Autre",
        color: "#64748b",
        bg: "#f1f5f9",
        plot: "#cbd5e1",
        opacity: 0.4,
    },
    5: {
        label: "Calculée",
        color: "#7c3aed",
        bg: "#ede9fe",
        plot: "#a78bfa",
        opacity: 0.6,
    },
}

/** Descripteur d'un code (fallback "Autre" si code inconnu). */
export function quality(code: number | null | undefined): QualityDescriptor {
    return QUALITY[code as ProvenanceCode] ?? QUALITY[4]
}

/** Ordre d'affichage de la légende (du plus fiable au moins officiel). */
export const QUALITY_LEGEND_ORDER: ProvenanceCode[] = [0, 1, 2, 3, 4]

/** Opacité d'un code (fallback 0.4). */
export function qualityOpacity(code: number | null | undefined): number {
    return quality(code).opacity
}

/**
 * Palette de couleurs des SÉRIES (graphes) — couleurs maison sobres. La couleur
 * distingue les variables/séries ; la qualité est portée par l'opacité.
 */
export const SERIES_PALETTE = [
    "#3D6FD8", // bleu
    "#2FB6A8", // turquoise
    "#7C6CE0", // violet
    "#E0823D", // orange
    "#5A9D58", // vert
    "#D85C8A", // rose
]

/** Couleur de série par index (cyclique). */
export function seriesColor(index: number): string {
    return SERIES_PALETTE[index % SERIES_PALETTE.length]
}
